// src/pages/Dashboard.jsx
import React, { useEffect, useState } from 'react';
import {
  Box, Typography, Button, Paper, Grid, CircularProgress,
  IconButton, Tooltip, useMediaQuery
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import MonetizationOnIcon from '@mui/icons-material/MonetizationOn';
import KeyIcon from '@mui/icons-material/VpnKey';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import AssignmentIndIcon from '@mui/icons-material/AssignmentInd';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import LockResetIcon from '@mui/icons-material/LockReset';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';

function Dashboard() {
  const navigate = useNavigate();
  const isMobile = useMediaQuery('(max-width:600px)');

  // States
  const [totalUsuarios, setTotalUsuarios] = useState(0);
  const [totalLicencas, setTotalLicencas] = useState(0);
  const [licencasAtivas, setLicencasAtivas] = useState(0);
  const [licencasExpiradas, setLicencasExpiradas] = useState(0);
  const [loading, setLoading] = useState(true);

  // Chave do usuário
  const chaveUsuario = localStorage.getItem('chave_usuario') || '';
  const [copied, setCopied] = useState(false);

  const username = localStorage.getItem('user') || 'Usuário';
  const userTipo = localStorage.getItem('tipo');

  // Dados de vencimento
  const dataVencimento = localStorage.getItem('data_vencimento');
  let diasRestantes = '';
  let isVencido = false;
  if (dataVencimento) {
    const hoje = new Date();
    const venc = new Date(dataVencimento);
    hoje.setHours(0, 0, 0, 0);
    venc.setHours(0, 0, 0, 0);
    const diff = Math.ceil((venc - hoje) / (1000 * 60 * 60 * 24));
    diasRestantes = diff >= 0 ? `Faltam ${diff} dia${diff === 1 ? '' : 's'}` : 'VENCIDO';
    isVencido = diff < 0;
  }

  const fetchData = async () => {
    setLoading(true);
    try {
      const res = await api.get('/dashboard');
      const dados = res.data;
      setTotalUsuarios(dados.totalUsuarios);
      setTotalLicencas(dados.totalLicencas);
      setLicencasAtivas(dados.licencasAtivas);
      setLicencasExpiradas(dados.licencasExpiradas);
    } catch (err) {
      console.error('Erro ao buscar dados do dashboard:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) {
      navigate('/login');
    } else {
      fetchData();
    }
  }, [navigate]);

  const handleLogout = () => {
    try {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      localStorage.removeItem('tipo');
      navigate('/login');
    } catch (err) {
      console.error('Erro ao fazer logout:', err);
    }
  };

  // Copiar chave usuário
  const handleCopyChave = () => {
    navigator.clipboard.writeText(chaveUsuario);
    setCopied(true);
    setTimeout(() => setCopied(false), 1200);
  };

  if (loading) {
    return (
      <Box p={4} display="flex" justifyContent="center" alignItems="center" height="60vh">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box
      p={isMobile ? 1 : 4}
      minHeight="100vh"
      sx={{ background: "linear-gradient(135deg, #FFF8E1 0%, #FFFFFF 80%)" }}
      position="relative"
    >
      {/* CHAVE E DATA DE VENCIMENTO */}
      <Box
        sx={{
          position: 'absolute',
          top: isMobile ? 15 : 25,
          right: isMobile ? 6 : 32,
          zIndex: 10,
          minWidth: 220,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-end',
          gap: 1.2
        }}
      >
        {/* Bloco da CHAVE */}
        <Paper
          elevation={4}
          sx={{
            px: 2.4,
            py: 0.8,
            display: 'flex',
            alignItems: 'center',
            gap: 1.1,
            background: '#fff8e1',
            border: '2px solid #fbc02d',
            borderRadius: '18px',
            minWidth: 140,
            maxWidth: 320,
            boxShadow: '0px 2px 8px #fbc02d44'
          }}
        >
          <KeyIcon fontSize="small" sx={{ color: '#fbc02d' }} />
          <Typography
            variant="subtitle2"
            sx={{ color: '#fbc02d', fontWeight: 'bold', fontSize: 13.5, letterSpacing: '0.02em' }}
          >
            CHAVE:
          </Typography>
          <Typography
            variant="body2"
            sx={{
              fontFamily: 'monospace',
              fontWeight: 'bold',
              fontSize: 15,
              letterSpacing: '0.12em',
              maxWidth: 140,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              color: '#555'
            }}
            title={chaveUsuario}
          >
            {chaveUsuario}
          </Typography>
          <Tooltip title={copied ? 'Copiado!' : 'Copiar'}>
            <IconButton onClick={handleCopyChave} size="small">
              <ContentCopyIcon fontSize="small" color={copied ? 'success' : 'action'} />
            </IconButton>
          </Tooltip>
        </Paper>

        {/* Badge da Data de Vencimento */}
        {dataVencimento && (
          <Paper
            elevation={4}
            sx={{
              px: 2.4,
              py: 0.8,
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              borderRadius: '18px',
              ...(isVencido
                ? {
                    bgcolor: '#ffebee',
                    border: '2px solid #e57373',
                    boxShadow: '0px 2px 8px #ef9a9a66',
                    color: '#b71c1c'
                  }
                : {
                    bgcolor: '#fff8e1',
                    border: '2px solid #fbc02d',
                    boxShadow: '0px 2px 8px #fbc02d44',
                    color: '#e65100'
                  })
            }}
          >
            {isVencido ? (
              <HighlightOffIcon fontSize="small" sx={{ color: '#e57373' }} />
            ) : (
              <CheckCircleOutlineIcon fontSize="small" sx={{ color: '#fbc02d' }} />
            )}

            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
              Vence em:{' '}
              <Box
                component="span"
                sx={{ fontFamily: 'monospace', fontWeight: 700, color: isVencido ? '#b71c1c' : '#555' }}
              >
                {new Date(dataVencimento).toLocaleDateString('pt-BR')}
              </Box>
            </Typography>

            <Typography variant="subtitle2" sx={{ fontWeight: 700, ml: 1 }}>
              — {diasRestantes}
            </Typography>
          </Paper>
        )}
      </Box>

      <Typography variant={isMobile ? 'h5' : 'h4'} gutterBottom fontWeight={600}>
        Painel de Licenças - Dashboard
      </Typography>
      <Typography variant={isMobile ? 'subtitle1' : 'h6'} gutterBottom fontWeight={500}>
        Bem-vindo, {username}!
      </Typography>

      <Grid container spacing={2} mt={1.5}>
        <Grid item xs={12} sm={6} md={3}>
          <Paper
            elevation={3}
            sx={{
              padding: 2,
              borderRadius: 3,
              boxShadow: '0 2px 8px #e3f2fd',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 1.5
            }}
          >
            <VerifiedUserIcon sx={{ fontSize: 36, color: '#2196f3' }} />
            <Typography variant="h6">Total Licenças</Typography>
            <Typography variant="h4" fontWeight={700}>
              {totalLicencas}
            </Typography>
          </Paper>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Paper
            elevation={3}
            sx={{
              padding: 2,
              borderRadius: 3,
              boxShadow: '0 2px 8px #e8f5e9',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 1.5
            }}
          >
            <CheckCircleOutlineIcon sx={{ fontSize: 36, color: '#43a047' }} />
            <Typography variant="h6">Licenças Ativas</Typography>
            <Typography variant="h4" fontWeight={700}>
              {licencasAtivas}
            </Typography>
          </Paper>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Paper
            elevation={3}
            sx={{
              padding: 2,
              borderRadius: 3,
              boxShadow: '0 2px 8px #ffebee',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 1.5
            }}
          >
            <HighlightOffIcon sx={{ fontSize: 36, color: '#d32f2f' }} />
            <Typography variant="h6">Licenças Expiradas</Typography>
            <Typography variant="h4" fontWeight={700}>
              {licencasExpiradas}
            </Typography>
          </Paper>
        </Grid>

        {userTipo === 'admin' && (
          <Grid item xs={12} sm={6} md={3}>
            <Paper
              elevation={3}
              sx={{
                padding: 2,
                borderRadius: 3,
                boxShadow: '0 2px 8px #e1bee7',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 1.5
              }}
            >
              <AssignmentIndIcon sx={{ fontSize: 36, color: '#8e24aa' }} />
              <Typography variant="h6">Total Usuários</Typography>
              <Typography variant="h4" fontWeight={700}>
                {totalUsuarios}
              </Typography>
            </Paper>
          </Grid>
        )}

        {/* CARD MINHAS VENDAS */}
        <Grid item xs={12} sm={6} md={3}>
          <Paper
            elevation={3}
            sx={{
              padding: 2,
              background: '#9AF39A',
              borderRadius: 3,
              boxShadow: '0 2px 8px #65C765',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 1.5
            }}
          >
            <MonetizationOnIcon sx={{ fontSize: 36, color: '#009688' }} />
            <Typography variant="h6">Minhas Vendas</Typography>
            <Button
              variant="contained"
              color="success"
              sx={{
                mt: 1,
                borderRadius: 99,
                fontWeight: 'bold',
                boxShadow: '0 1px 6px #b2dfdb',
                transition: 'all .15s',
                '&:hover': { transform: 'scale(1.05)' }
              }}
              onClick={() => navigate('/listar-cobrancas-Pix')}
              fullWidth
            >
              VER VENDAS
            </Button>
          </Paper>
        </Grid>

        {/* CARD HISTÓRICO KIT BLINDAGEM */}
        <Grid item xs={12} sm={6} md={3}>
          <Paper
            elevation={3}
            sx={{
              padding: 2,
              background: '#f3e5f5',
              borderRadius: 3,
              boxShadow: '0 2px 8px #e1bee7',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 1.5
            }}
          >
            <AssignmentIndIcon sx={{ fontSize: 36, color: '#6a1b9a' }} />
            <Typography variant="h6">Histórico Kit Blindagem</Typography>
            <Button
              variant="contained"
              color="secondary"
              sx={{
                mt: 1,
                borderRadius: 99,
                fontWeight: 'bold',
                boxShadow: '0 1px 6px #ce93d8',
                transition: 'all .15s',
                '&:hover': { transform: 'scale(1.05)' }
              }}
              onClick={() => navigate('/listar-kit-historico')}
              fullWidth
            >
              VER HISTÓRICO
            </Button>
          </Paper>
        </Grid>
      </Grid>

      {/* BOTÕES FINAIS */}
      <Box mt={4} display="flex" flexWrap="wrap" gap={2}>
        <Button
          variant="contained"
          color="primary"
          onClick={() => navigate('/licencas')}
          sx={{ fontWeight: 700, borderRadius: 2, px: 2.5 }}
        >
          IR PARA LICENÇAS
        </Button>

        {userTipo === 'admin' && (
          <Button
            variant="contained"
            color="secondary"
            onClick={() => navigate('/usuarios')}
            sx={{ fontWeight: 700, borderRadius: 2, px: 2.5 }}
          >
            IR PARA USUÁRIOS
          </Button>
        )}

        <Button
          variant="contained"
          color="info"
          startIcon={<LockResetIcon />}
          onClick={() => navigate('/minha-conta/trocar-senha')}
          sx={{
            fontWeight: 700,
            borderRadius: 2,
            px: 2.5,
            boxShadow: '0 1px 6px rgba(2,136,209,.25)',
            '&:hover': { transform: 'translateY(-1px)' },
            transition: 'transform .15s ease'
          }}
        >
          TROCAR SENHA
        </Button>

        <Button
          variant="contained"
          color="error"
          onClick={handleLogout}
          sx={{
            fontWeight: 700,
            borderRadius: 2,
            px: 2.5,
            boxShadow: '0 1px 6px rgba(211,47,47,.25)',
            '&:hover': { transform: 'translateY(-1px)' },
            transition: 'transform .15s ease'
          }}
        >
          LOGOUT
        </Button>
      </Box>
    </Box>
  );
}

export default Dashboard;
